import type { ReactElement } from 'react';
import { Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

/**
 * Rendering shared by the audit history's two views — the change list and the version comparison
 * grid — so a value looks the same whichever one you are reading.
 */

export const changeTypeConfig: Record<string, { icon: ReactElement; color: 'success' | 'info' | 'error'; label: string }> = {
  Created: { icon: <AddIcon fontSize="small" />, color: 'success', label: 'Created' },
  Updated: { icon: <EditIcon fontSize="small" />, color: 'info', label: 'Updated' },
  Deleted: { icon: <DeleteIcon fontSize="small" />, color: 'error', label: 'Deleted' },
};


/**
 * Strips the JSON quoting the audit tables store values with, so the caller can measure or render the
 * value as the user will read it.
 */
export function displayValue(value: string | null): string | null {
  if (value === null || value === '' || value === '""') return null;
  return value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1) : value;
}

/** Longer than this and a value is worth collapsing rather than letting it set the row height. */
export const LONG_VALUE_LENGTH = 60;

export function ValueDisplay({
  value,
  derived = false,
  clampLines,
  label,
}: {
  value: string | null;
  derived?: boolean;
  /** Collapse to this many lines. Omitted means render in full, as the change list does. */
  clampLines?: number;
  /**
   * Readable form of the value — an enum name, or the label of the referenced record. Shown instead
   * of the raw value, which stays reachable in the tooltip: a guid is what was recorded, but nobody
   * reads a history in guids.
   */
  label?: string | null;
}) {
  const { t } = useTranslation();

  if (value === null || value === '' || value === '""') {
    return (
      <Typography variant="body2" color="text.disabled" fontStyle="italic">
        -
      </Typography>
    );
  }
  // Strip surrounding quotes from JSON string values
  const raw = value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1) : value;
  const display = label ?? raw;

  const clamped = clampLines !== undefined && display.length > LONG_VALUE_LENGTH;

  const text = (
    <Typography
      variant="body2"
      data-clamped={clamped ? 'true' : undefined}
      sx={{
        wordBreak: 'break-word',
        // Muted AND italic: the distinction must survive without colour.
        ...(derived ? { color: 'text.secondary', fontStyle: 'italic' } : {}),
        ...(clamped
          ? {
              display: '-webkit-box',
              WebkitLineClamp: clampLines,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }
          : {}),
      }}
    >
      {display}
    </Typography>
  );

  const notes = [
    label ? t('audit.recordedValue', { defaultValue: 'Recorded as: {{value}}', value: raw }) : null,
    derived
      ? t('audit.derivedValue', {
          defaultValue: 'Not recorded at the time — worked out from later changes and the current record.',
        })
      : null,
  ].filter(Boolean);

  if (notes.length === 0) return text;

  return <Tooltip title={notes.join(' · ')}>{text}</Tooltip>;
}

export function formatTimestamp(date: Date): string {
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function humanizeFieldName(field: string): string {
  // "orderDate" → "Order Date", "totalAmount" → "Total Amount"
  return field
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, s => s.toUpperCase());
}

export function humanizeCommandName(command: string): string {
  // "POST /api/sales/order" → "Save"
  // "ConfirmOrder" → "Confirm Order"
  // "PUT /api/..." → "Update"
  if (command.startsWith('POST ')) return 'Created';
  if (command.startsWith('PUT ')) return 'Updated';
  if (command.startsWith('DELETE ')) return 'Deleted';
  return command
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, s => s.toUpperCase());
}
